/**
 * GERI SAYIM FAZI — TURETILIR, SAKLANMAZ.
 *
 * ============================ KIM HESAPLIYOR ================================
 * FAZI SUNUCU HESAPLAR. `/v1/tasks` ve `/v1/offers` her satirda hazir gonderir:
 *   `countdownStatus` (offer | decide | payment | passive), `countdown` (kalan
 *   saniye) ve `countdownDuration`. Istemci KENDI faz makinesini kurmaz.
 *
 * Istemcinin isi iki tane:
 *   1. Sunucunun verdigi fazi GORSELLESTIRMEK.
 *   2. Sayac dolunca satiri YERELDE bir sonraki faza gecirmek — liste yeniden
 *      cekilmesin diye. (Mobilde dolan HER sayac tum listeyi tazeliyordu.)
 *
 * Asagidaki pencereler sunucunun kullandiklarinin AYNASI; yeni bir kural degil,
 * ayni hesabin istemci tarafindaki kopyasi:
 *
 *      teklif toplama   0 sn  ..  120 sn      -> 'offer'
 *      karar            120 sn ..  240 sn     -> 'decide'
 *      odeme            0 sn  ..  900 sn      -> 'payment'   (KABUL EDILMIS gorevde)
 *      sonrasi                                -> 'passive'
 *
 * `payment` ZINCIRIN DISINDA: 'decide' dolunca gorev KAPANIR; odemeye ancak
 * musteri bir teklifi KABUL edince (taskStatusCode -> 'pending') gecilir.
 * Buronun "Odeme alindi" onayi ('payment_received', 2026-09-26) AYNI 15 dakikalik
 * pencereyi yeniden baslatir — bu kez buronun GONDERIM suresi. Sunucu iki durumu
 * da ayni 'payment' fazinda tasiyor (db-scripts.js PAYMENT_PHASE_STATUSES); kim ne
 * yapacak sorusu `taskStatusCode`'dan okunur (bkz. phaseLabelKey).
 *
 * ================================= SAAT =====================================
 * Cagiran taraf `nowMs` olarak serverNow() (src/lib/clock-skew.ts) verir; bu
 * modul Date.now()'a HIC bakmaz. Saati yanlis kurulmus bir cihaz, dogru veriye
 * ragmen yanlis faz gosterirdi — ve bu, kullanicinin sikayet edecegi ama
 * loglarda izi olmayan turden bir hatadir. Saf tutulmasinin ikinci sebebi:
 * sinir saniyeleri ancak enjekte edilebilir bir saatle sinanabilir.
 */

import { isActiveAcceptedStatus } from './task-status';

export const COUNTDOWN_PHASES = ['offer', 'decide', 'payment', 'passive'] as const;
export type CountdownPhase = (typeof COUNTDOWN_PHASES)[number];

/**
 * Sunucudaki sabit pencereler, SANIYE.
 *
 * `offer` ve `decide` degerleri `status_updated_at`'ten itibaren KUMULATIFTIR:
 * decide 120..240 arasidir, 120..360 degil. `payment` kendi basina 900
 * saniyelik tek penceredir.
 */
export const OFFER_WINDOW_SECONDS = 120;
export const DECIDE_WINDOW_SECONDS = 240;
export const PAYMENT_WINDOW_SECONDS = 900;

/**
 * Bir satirin hangi ZINCIRDE oldugu.
 *   'open'    : acik talep      -> offer -> decide -> passive
 *   'payment' : kabul edilmis   -> payment -> passive
 *   'closed'  : sayan bir sey yok
 */
export type PhaseChain = 'open' | 'payment' | 'closed';

/** Sayac dolunca gidilen faz. Zincir SABIT ve sunucuyla ayni. */
const NEXT_PHASE: Readonly<Record<CountdownPhase, CountdownPhase>> = Object.freeze({
  offer: 'decide',
  decide: 'passive',
  payment: 'passive',
  passive: 'passive',
});

export const nextPhase = (phase: CountdownPhase): CountdownPhase => NEXT_PHASE[phase];

export const isCountdownPhase = (value: unknown): value is CountdownPhase =>
  typeof value === 'string' && (COUNTDOWN_PHASES as readonly string[]).includes(value);

/**
 * Faz rozetinin METIN anahtari (tr.ts kok seviyesi).
 *
 * Faz dort kok anahtardan birine duser; TEK istisna 'payment_received': sunucu onu da
 * 'payment' fazinda tasiyor ama musteri odemesini YAPMIS ve buronun gonderimi bekleniyor.
 * "Odeme Bekliyor" yazmak iki tarafa da yanlis bilgi olurdu. 'payout' sunucudan GELMEZ,
 * burada turetilir (mobil PhaseBadge ile ayni kural). Kapali fazda durum etiketi zaten
 * QuoteHeader'in meta satirinda (TASK_STATUS_LABEL_KEYS).
 */
export const phaseLabelKey = (phase: CountdownPhase, taskStatusCode?: string | null): string =>
  phase === 'payment' && taskStatusCode === 'payment_received' ? 'payout' : phase;

export type PhaseResult = {
  phase: CountdownPhase;
  /** Bu fazin bitmesine kalan sure (ms). Faz 'passive' ise 0. */
  remainingMs: number;
  /** Bu fazin TOPLAM uzunlugu (ms) — halka ilerlemesi bunun uzerinden cizilir. */
  durationMs: number;
};

const PASSIVE: PhaseResult = Object.freeze({ phase: 'passive', remainingMs: 0, durationMs: 0 });

const result = (phase: CountdownPhase, remainingMs: number, durationMs: number): PhaseResult => ({
  phase,
  // Negatife dusmesi mumkun degil ama bir tik gecikmesi ekranda "-1 sn" gosterirdi.
  remainingMs: Math.max(0, remainingMs),
  durationMs,
});

/**
 * Zincir secimi. SUNUCUNUN FAZI ONCELIKLI — 'payment' gormussek satir kabul
 * edilmis demektir, `taskStatusCode`'un ne dedigi ikinci plandadir.
 * 'pending' ve 'payment_received' ikisi de odeme zincirinde (lib/task-status.ts).
 */
export const chainOf = (
  serverPhase: CountdownPhase | null,
  taskStatusCode: string | null | undefined,
): PhaseChain => {
  if (serverPhase === 'payment' || isActiveAcceptedStatus(taskStatusCode)) return 'payment';
  if (serverPhase === 'offer' || serverPhase === 'decide') return 'open';
  if (serverPhase === 'passive') return 'closed';
  // Sunucu faz gondermediyse gorev durumundan karar verilir.
  if (taskStatusCode === 'new' || taskStatusCode === null || taskStatusCode === undefined) {
    return 'open';
  }
  return 'closed';
};

/**
 * `status_updated_at`'ten gecen sureye gore faz. SUNUCU HESABININ AYNASI.
 *
 * @param elapsedMs fazin baslangicindan (status_updated_at) bu yana gecen sure
 * @param chain hangi pencere zinciri gecerli
 */
export const phaseFromElapsed = (elapsedMs: number, chain: PhaseChain): PhaseResult => {
  if (chain === 'closed') return PASSIVE;
  const elapsed = Math.max(0, elapsedMs);

  if (chain === 'payment') {
    const window = PAYMENT_WINDOW_SECONDS * 1000;
    if (elapsed >= window) return PASSIVE;
    return result('payment', window - elapsed, window);
  }

  const offerEnd = OFFER_WINDOW_SECONDS * 1000;
  const decideEnd = DECIDE_WINDOW_SECONDS * 1000;
  if (elapsed < offerEnd) return result('offer', offerEnd - elapsed, offerEnd);
  if (elapsed < decideEnd) return result('decide', decideEnd - elapsed, decideEnd - offerEnd);
  return PASSIVE;
};

/** Faz hesabinin ihtiyaci olan asgari satir bilgisi. */
export type PhaseInput = {
  /** Sunucudaki `taskStatusCode`: 'new' | 'pending' | 'payment_received' | 'cancelled' … */
  taskStatusCode?: string | null | undefined;
  /** Sunucunun HESAPLADIGI faz. Baslangic noktasi budur. */
  countdownStatus?: string | null | undefined;
  /**
   * Fazin baslangici. `/v1/tasks` gonderir, `/v1/offers` GONDERMEZ — bu yuzden
   * ikinci bir yol (asagidaki `countdown`) sart.
   */
  statusUpdatedAt?: string | null | undefined;
  /** Yanit ANINDA kalan saniye. Mutlak bir zaman degil, o ana GORE bir sure. */
  countdown?: number | null | undefined;
  /**
   * Satirin alindigi an (serverNow()). `countdown` goreli oldugu icin mutlak bir
   * bitis anina cevrilmesi SART; aksi halde ekranda duran bir liste, her
   * render'da bastan sayan bir sayac gosterir.
   */
  receivedAtMs: number;
};

/**
 * Bir satirin SU ANDAKI fazi.
 *
 * Sira:
 *   0. Sunucu 'passive' dediyse tartisma yok — kapali.
 *   1. `statusUpdatedAt` varsa: sunucunun kullandigi GIRDININ AYNISIYLA ayni
 *      hesap yapilir. Sapma duzeltilmis saatle, sayfa saatlerce acik kalsa bile
 *      dogru kalir.
 *   2. Yoksa (teklif satirlari) `countdown` alindigi ana eklenerek mutlak bir
 *      bitise cevrilir; sayac dolunca faz zincirde BIR ADIM ilerler.
 */
export const phaseOf = (input: PhaseInput, nowMs: number): PhaseResult => {
  const serverPhase = isCountdownPhase(input.countdownStatus) ? input.countdownStatus : null;
  if (serverPhase === 'passive') return PASSIVE;

  const chain = chainOf(serverPhase, input.taskStatusCode);
  if (chain === 'closed') return PASSIVE;

  const startedAt = input.statusUpdatedAt ? Date.parse(input.statusUpdatedAt) : Number.NaN;
  if (Number.isFinite(startedAt)) {
    return phaseFromElapsed(nowMs - startedAt, chain);
  }

  // --- `countdown` yolu ---------------------------------------------------
  if (serverPhase === null) return PASSIVE;

  const countdown = input.countdown;
  if (typeof countdown !== 'number' || !Number.isFinite(countdown) || countdown <= 0) {
    return result(nextPhase(serverPhase), 0, 0);
  }

  const deadlineMs = input.receivedAtMs + countdown * 1000;
  const remaining = deadlineMs - nowMs;
  if (remaining <= 0) return result(nextPhase(serverPhase), 0, 0);

  // Pencere uzunlugu satirda gelmediyse fazin BILINEN uzunlugu kullanilir.
  const durationSeconds =
    serverPhase === 'offer'
      ? OFFER_WINDOW_SECONDS
      : serverPhase === 'decide'
        ? DECIDE_WINDOW_SECONDS - OFFER_WINDOW_SECONDS
        : PAYMENT_WINDOW_SECONDS;

  return result(serverPhase, remaining, durationSeconds * 1000);
};

/** Halkanin doluluk orani (0..1). Sure bilinmiyorsa dolu kabul edilir. */
export const phaseProgress = (phase: PhaseResult): number => {
  if (phase.durationMs <= 0) return 1;
  return Math.min(1, Math.max(0, 1 - phase.remainingMs / phase.durationMs));
};

/** mm:ss. Sayac bittiginde '0:00'. */
export const formatRemaining = (remainingMs: number): string => {
  const total = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes)}:${seconds < 10 ? '0' : ''}${String(seconds)}`;
};
